import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, Checkbox } from "antd";

import { login } from "./../../app.slice";
import { useLoginUserMutation } from "./login.service";
import { useCompleteWizardMutation } from "../register/register.service";
import { useTranslation } from "react-i18next";
import FirstLoginWizard from "../../pages/Wizard/wizard";
import "./style.css";
import { ROLE } from "../../constant";
import { isOwnerAccessRestricted } from "../../utils/subscriptionAccess";

export default function Login() {
  const [params] = useSearchParams();
  const redirectTo = params.get("redirect");
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [rememberDetailState, setRememberDetailState] = useState(false);
  const [showWizard, setShowWizard] = useState(false);

  const [loginUser, { isLoading, isSuccess }] = useLoginUserMutation();
  const [completeWizard] = useCompleteWizardMutation();
  const userData = useSelector((apps) => apps.app.user);

  const onFinish = async (values) => {
    try {
      const response = await loginUser(values).unwrap();
      dispatch(login(response));
      const loggedInUser = response?.data?.user;

      const shouldShowWizard =
        (response?.data.isFirstLogin === true ||
          response?.data?.user?.isFirstLogin === 0) &&
        loggedInUser?.role !== ROLE.SUPER_ADMIN &&
        !isOwnerAccessRestricted(loggedInUser);

      if (shouldShowWizard) {
        setShowWizard(true);
      } else {
        handleRedirect(loggedInUser);
      }
    } catch (err) {
      console.log("❌ Login error:", err);
    }
  };

  const handleRedirect = (loggedInUser) => {
    const user = loggedInUser || userData;
    if (isOwnerAccessRestricted(user)) {
      navigate("/account", { replace: true });
      return;
    }
    if (redirectTo && redirectTo !== "/home") {
      navigate(redirectTo, { replace: true });
    } else if (user?.role === ROLE.SUPER_ADMIN) {
      navigate("/restaurant", { replace: true });
    } else {
      navigate("/home", { replace: true });
    }
  };

  const handleWizardComplete = async (wizardData) => {
    try {
      const response = await completeWizard(wizardData).unwrap();
      if (response.success) {
        setShowWizard(false);
        localStorage.setItem("wizardJustCompleted", "true");
        handleRedirect(userData);
      }
      return response;
    } catch (error) {
      console.error("❌ Wizard completion failed:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (isSuccess && !showWizard) {
      if (rememberDetailState) {
        localStorage.setItem("rememberedUsername", form.getFieldValue("email"));
        localStorage.setItem(
          "rememberedPassword",
          form.getFieldValue("password")
        );
        localStorage.setItem("rememberDetails", "true");
      }

      if (redirectTo && redirectTo.includes("/account")) {
        sessionStorage.setItem("fromLoginRedirect", "true");
      }
    }
  }, [isSuccess, showWizard, rememberDetailState, form, redirectTo]);

  const onChangeRememberDetail = (e) => {
    if (!e.target.checked) {
      localStorage.removeItem("rememberedUsername");
      localStorage.removeItem("rememberedPassword");
      localStorage.removeItem("rememberDetails");
    }
    setRememberDetailState(e.target.checked);
  };

  useEffect(() => {
    const savedUsername = localStorage.getItem("rememberedUsername");
    const savedPassword = localStorage.getItem("rememberedPassword");
    const rememberFlag = localStorage.getItem("rememberDetails") === "true";

    if (savedUsername && savedPassword && rememberFlag) {
      form.setFieldsValue({ email: savedUsername, password: savedPassword });
      setRememberDetailState(true);
    }
  }, [form]);

  return (
    <>
      <div className="signincontainer signup-page auth-page">
        <div className="saas-card auth-card">
          <div className="auth-brand">
            <div className="auth-logo-wrap">
              <img src="/mark1.jpg" alt="SFBB Pro" />
            </div>
          </div>

          <div className="auth-heading">Log in to your account</div>

          <Form
            onFinish={onFinish}
            form={form}
            layout="vertical"
            className="auth-form"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label="Email/Username"
              rules={[
                {
                  required: true,
                  message: "Please input your email or username!",
                },
              ]}
            >
              <Input
                placeholder="Enter your email or username"
                className="auth-input"
                size="large"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Please input your password!" },
              ]}
            >
              <Input.Password
                placeholder="Enter your password"
                className="auth-input"
                size="large"
                autoComplete="current-password"
              />
            </Form.Item>

            <div className="auth-row">
              <Checkbox
                className="auth-remember"
                checked={rememberDetailState}
                onChange={onChangeRememberDetail}
              >
                Remember details
              </Checkbox>
              <NavLink className="auth-text-link" to="/forgot-password">
                Forgotten password?
              </NavLink>
            </div>

            <Button
              htmlType="submit"
              size="large"
              block
              loading={isLoading}
              className="signup-cta-btn"
            >
              {t("LOGIN") || "LOG IN"}
            </Button>
          </Form>

          <div className="auth-footer-note">
            Sign up for a free 14-day trial{" "}
            <NavLink to="/create-account" className="auth-text-link strong">
              Create account
            </NavLink>
          </div>
        </div>
      </div>

      <FirstLoginWizard
        visible={showWizard}
        onComplete={() => {
          setShowWizard(false);
          handleRedirect();
        }}
        onSubmitWizard={handleWizardComplete}
      />
    </>
  );
}
